import { useSeoMeta } from '@unhead/react';
import { Link } from 'react-router-dom';
import { SiteLayout } from '@/components/SiteLayout';
import { Button } from '@/components/ui/button';

const TIPS: { title: string; body: React.ReactNode }[] = [
  {
    title: '1. Read the prompt',
    body: 'Each problem describes a function to write and shows a few example calls with the expected results.',
  },
  {
    title: '2. Write the function',
    body: (
      <>
        The editor starts with the function signature. Fill in the body and <code className="font-mono">return</code> the
        answer — don&apos;t <code className="font-mono">console.log</code> it. Keep the function name the same.
      </>
    ),
  },
  {
    title: '3. Press Go',
    body: 'Your code runs against every test case. Green OK means a test passed; red X shows what your code returned instead.',
  },
  {
    title: '4. Iterate',
    body: 'Fix the failing cases and run again. Once every test passes, the problem is marked solved. Your code and progress are saved in this browser.',
  },
];

export default function HelpPage() {
  useSeoMeta({
    title: 'Help — JSBat',
    description: 'How JSBat works: read the prompt, write a JavaScript function, press Go, and get instant test feedback.',
  });

  return (
    <SiteLayout>
      <div className="mx-auto max-w-3xl px-4 py-12 sm:px-6">
        <h1 className="text-4xl font-extrabold tracking-tight sm:text-5xl">How JSBat works</h1>
        <p className="mt-4 text-lg leading-relaxed text-muted-foreground">
          JSBat is a collection of small JavaScript problems for building coding fluency — the kind of reps that make
          loops, strings, arrays and logic feel automatic.
        </p>

        <div className="mt-10 grid gap-4 sm:grid-cols-2">
          {TIPS.map((t) => (
            <div key={t.title} className="rounded-xl border bg-card p-5 shadow-sm">
              <h2 className="font-semibold">{t.title}</h2>
              <p className="mt-2 leading-relaxed text-muted-foreground">{t.body}</p>
            </div>
          ))}
        </div>

        <h2 className="mt-12 text-2xl font-bold tracking-tight">Good to know</h2>
        <ul className="mt-4 list-disc space-y-3 pl-6 text-lg leading-relaxed">
          <li>Modern JavaScript is supported: <code className="font-mono">const</code>/<code className="font-mono">let</code>, arrow functions, template literals, spread, array methods, regular expressions and more.</li>
          <li>Arrays and objects are compared by value, so <code className="font-mono">[1, 2]</code> equals <code className="font-mono">[1, 2]</code>.</li>
          <li>Code runs in a sandboxed interpreter inside your browser. Infinite loops are caught and stopped automatically.</li>
          <li>Warmup-1 solutions are always visible. For other sections, make an attempt first to unlock the solution.</li>
          <li>Press <kbd className="rounded border bg-muted px-1.5 font-mono text-sm">Ctrl</kbd>/<kbd className="rounded border bg-muted px-1.5 font-mono text-sm">⌘</kbd> + <kbd className="rounded border bg-muted px-1.5 font-mono text-sm">Enter</kbd> in the editor to run. <kbd className="rounded border bg-muted px-1.5 font-mono text-sm">Tab</kbd> indents.</li>
        </ul>

        <Button asChild size="lg" className="mt-10 bg-brand text-brand-foreground hover:bg-brand/90">
          <Link to="/problems/Warmup-1/sleepIn">Try the first problem</Link>
        </Button>
      </div>
    </SiteLayout>
  );
}
